import type { Course, DaySchedule } from '../types';
import { parseStartTime } from './timeUtils';

type DayName = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

interface TimetableProfileScope {
  instituteKeywords?: string[];
  departmentKeywords?: string[];
  programKeywords?: string[];
  category?: 'bachelor' | 'master' | 'any';
}

interface TimetableProfile {
  name: string;
  effectiveFrom: string;
  effectiveTo?: string;
  scope: TimetableProfileScope;
  periodsByDayGroup: {
    monThu?: string[];
    fri?: string[];
  };
}

type ProfileActivationMode = 'pending' | 'auto' | 'active' | 'inactive';

interface TimetableProfileControl {
  mode?: ProfileActivationMode;
  officialStartDate?: string;
  officialEndDate?: string;
  predictedStartDate?: string;
  predictedEndDate?: string;
}

interface TimetableProfileControlFile {
  profiles?: Record<string, TimetableProfileControl>;
}

interface TimetableProfileContext {
  institute?: string;
  department?: string;
  program?: string;
  referenceDate?: Date;
}

const DEFAULT_PROFILE_MODE: ProfileActivationMode = 'pending';
let runtimeProfileControls: Record<string, TimetableProfileControl> = {};

const TIMETABLE_PROFILES: TimetableProfile[] = [
  {
    name: 'IMS_Bachelors_Ramadan_2026',
    // Update dates if your official Ramadan moon-sighting dates change.
    effectiveFrom: '2026-02-19',
    effectiveTo: '2026-03-21',
    scope: {
      instituteKeywords: ['institute of management sciences', 'ims'],
      category: 'bachelor'
    },
    periodsByDayGroup: {
      monThu: [
        '08:30 AM-09:45 AM',
        '09:55 AM-11:10 AM',
        '11:20 AM-12:35 PM',
        '12:45 PM-02:00 PM'
      ],
      fri: [
        '08:30 AM-09:45 AM',
        '09:55 AM-11:10 AM',
        '11:20 AM-12:35 PM',
        '12:35 PM-01:45 PM'
      ]
    }
  }
];

const MON_THU_DAYS: DayName[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday'];
const FRIDAY: DayName = 'Friday';
const REGULAR_MON_THU_STARTS = [510, 600, 690, 840]; // 08:30, 10:00, 11:30, 14:00
const REGULAR_FRI_STARTS = [510, 600, 690, 810]; // 08:30, 10:00, 11:30, 13:30
const SLOT_MATCH_TOLERANCE_MINUTES = 45;

export async function loadTimetableProfileControl(): Promise<void> {
  try {
    const response = await fetch('/timetable-profile-control.json', { cache: 'no-store' });
    if (!response.ok) return;

    const payload = (await response.json()) as TimetableProfileControlFile;
    runtimeProfileControls = payload.profiles ?? {};
  } catch {
    runtimeProfileControls = {};
  }
}

function normalizeDateOnly(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function parseDateOrUndefined(dateStr?: string): Date | undefined {
  if (!dateStr) return undefined;
  return normalizeDateOnly(new Date(`${dateStr}T00:00:00`));
}

function isProfileActive(profile: TimetableProfile, referenceDate: Date): boolean {
  const control = runtimeProfileControls[profile.name];
  const mode = control?.mode ?? DEFAULT_PROFILE_MODE;

  if (mode === 'inactive') return false;
  if (mode === 'active') return true;

  const today = normalizeDateOnly(referenceDate);
  const officialFrom = parseDateOrUndefined(control?.officialStartDate);
  const officialTo = parseDateOrUndefined(control?.officialEndDate);

  // pending mode blocks activation until official start is provided.
  if (mode === 'pending' && !officialFrom) {
    return false;
  }

  const predictedFrom = parseDateOrUndefined(control?.predictedStartDate) ??
    normalizeDateOnly(new Date(`${profile.effectiveFrom}T00:00:00`));

  const predictedTo = parseDateOrUndefined(control?.predictedEndDate) ??
    (profile.effectiveTo ? normalizeDateOnly(new Date(`${profile.effectiveTo}T00:00:00`)) : undefined);

  const from = officialFrom ?? predictedFrom;
  const to = officialTo ?? predictedTo;

  if (today < from) return false;
  if (!to) return true;
  return today <= to;
}

function isBachelorProgram(programName: string): boolean {
  const normalized = programName.toLowerCase();
  return normalized.includes('bachelor') || normalized.startsWith('bs') || normalized.startsWith('b.s');
}

function includesKeyword(text: string, keywords?: string[]): boolean {
  if (!keywords || keywords.length === 0) return true;
  const lower = text.toLowerCase();
  return keywords.some(keyword => lower.includes(keyword.toLowerCase()));
}

function matchesScope(profile: TimetableProfile, context: TimetableProfileContext): boolean {
  const program = context.program ?? '';
  const department = context.department ?? '';
  const institute = context.institute ?? '';
  const combined = `${institute} ${department} ${program}`.trim();

  if (!includesKeyword(combined, profile.scope.instituteKeywords)) return false;
  if (!includesKeyword(department || combined, profile.scope.departmentKeywords)) return false;
  if (!includesKeyword(program || combined, profile.scope.programKeywords)) return false;

  if (profile.scope.category === 'bachelor' && !isBachelorProgram(program)) {
    return false;
  }

  return true;
}

function pickActiveProfile(context: TimetableProfileContext): TimetableProfile | null {
  const referenceDate = context.referenceDate ?? new Date();

  for (const profile of TIMETABLE_PROFILES) {
    if (isProfileActive(profile, referenceDate) && matchesScope(profile, context)) {
      return profile;
    }
  }

  return null;
}

function getNearestPeriodIndex(startMinutes: number, regularStarts: number[]): number {
  if (!Number.isFinite(startMinutes) || regularStarts.length === 0) return -1;
  let nearestIndex = 0;
  let smallestDiff = Math.abs(startMinutes - regularStarts[0]);

  for (let i = 1; i < regularStarts.length; i += 1) {
    const diff = Math.abs(startMinutes - regularStarts[i]);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      nearestIndex = i;
    }
  }

  return nearestIndex;
}

function applyDaySlots(classes: Course[], slots: string[], regularStarts: number[]): Course[] {
  return classes.map((cls) => {
    const startMinutes = parseStartTime(cls.time);
    if (!Number.isFinite(startMinutes)) {
      return cls;
    }

    const periodIndex = getNearestPeriodIndex(startMinutes, regularStarts);
    if (periodIndex < 0 || periodIndex >= slots.length) {
      return cls;
    }

    const distance = Math.abs(startMinutes - regularStarts[periodIndex]);
    if (distance > SLOT_MATCH_TOLERANCE_MINUTES) {
      return cls;
    }

    return {
      ...cls,
      time: slots[periodIndex] ?? cls.time
    };
  });
}

export function applyTimetableProfile(
  schedule: DaySchedule,
  context: TimetableProfileContext
): { schedule: DaySchedule; activeProfileName?: string } {
  const profile = pickActiveProfile(context);
  if (!profile) {
    return { schedule };
  }

  const updated: DaySchedule = { ...schedule };

  for (const day of MON_THU_DAYS) {
    const dayClasses = updated[day];
    if (!dayClasses || dayClasses.length === 0 || !profile.periodsByDayGroup.monThu) continue;
    updated[day] = applyDaySlots(dayClasses, profile.periodsByDayGroup.monThu, REGULAR_MON_THU_STARTS);
  }

  const fridayClasses = updated[FRIDAY];
  if (fridayClasses && fridayClasses.length > 0 && profile.periodsByDayGroup.fri) {
    updated[FRIDAY] = applyDaySlots(fridayClasses, profile.periodsByDayGroup.fri, REGULAR_FRI_STARTS);
  }

  return {
    schedule: updated,
    activeProfileName: profile.name
  };
}
